import { WorkContextOption } from '@fb/models';

/**
 * Visible text of an option: the server label, else its code. A group this build does not know yet
 * shows its first own string field other than `group` / `label`, else the group code itself.
 */
export function workContextLabel(option: WorkContextOption): string {
  if (option.label) {
    return option.label;
  }

  const code = workContextCode(option);

  if (code != null) {
    return code;
  }

  const unknown = option as { group: string };
  const value = Object.entries(unknown).find(
    ([field, fieldValue]) => (
      field !== 'group' && field !== 'label' && typeof fieldValue === 'string'
    ),
  );

  return value ? (value[1] as string) : unknown.group;
}

/**
 * Stable identity for `@for track` and selection — server JSON objects are not reference-stable.
 * An unknown group is keyed by its whole JSON: its fields may differ only in non-string values.
 */
export function workContextKey(option: WorkContextOption): string {
  return `${option.group}:${workContextCode(option) ?? JSON.stringify(option)}`;
}

/** Work scope or license of an option; null for a group this build does not know yet. */
function workContextCode(option: WorkContextOption): string | null {
  const { workScope, license } = workContextChatFields(option);

  return workScope ?? license ?? null;
}

/**
 * `workContext` request payload: the picked option without its display-only `label` and without
 * the `null` field of the other group the server may send.
 */
export function workContextRequest(option: WorkContextOption): WorkContextOption {
  const { label: _label, ...request } = option;

  return Object.fromEntries(
    Object.entries(request).filter(([, value]) => value != null),
  ) as WorkContextOption;
}

/** Picker close payload guard: a picked option vs cancel (`undefined`). */
export function isWorkContextOption(value: unknown): value is WorkContextOption {
  return typeof value === 'object' && value !== null && 'group' in value;
}

export interface WorkContextGroupView {
  group: string;
  options: WorkContextOption[];
}

/** Groups in order of first appearance; options keep response order inside a group. */
export function groupWorkContexts(options: readonly WorkContextOption[]): WorkContextGroupView[] {
  const groups: WorkContextGroupView[] = [];

  for (const option of options) {
    const existing = groups.find(item => item.group === option.group);

    if (existing) {
      existing.options.push(option);
    } else {
      groups.push({ group: option.group, options: [option] });
    }
  }

  return groups;
}

/** Chat fields a picked option fills (optimistic sidebar row, mocks). */
export function workContextChatFields(
  option: WorkContextOption | null | undefined,
): { license?: string; workScope?: string; workContextLabel?: string } {
  if (!option) {
    return {};
  }

  const label = option.label ? { workContextLabel: option.label } : {};

  // Value checks, not `in`: the server may send the other group's field as `null`.
  if ('workScope' in option && option.workScope != null) {
    return { workScope: option.workScope, ...label };
  }

  if ('license' in option && option.license != null) {
    return { license: option.license, ...label };
  }

  return {};
}

/** Badge for a "Work" chat: server label, else license, else work scope; null when none is set. */
export function workChatBadge(chat: {
  license?: string | null;
  workScope?: string | null;
  workContextLabel?: string | null;
}): string | null {
  return chat.workContextLabel || chat.license || chat.workScope || null;
}
